// frontend/src/components/AddAssetModal.js
/**
 * Add Asset Modal – create a new asset.
 * Uses AssetCreateUpdateSerializer (asset_type, asset_tag required).
 * Styling follows EditAssetModal and design-system-reference.md.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Package, Shield, CreditCard, Plus, Search, CheckCircle, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import Modal from './UI/Modal';
import FormSelect from './UI/FormSelect';
import DatalistCombobox from './UI/DatalistCombobox';
import { useAssetTypes, useCreateAsset, useVendors, useCreateVendor } from '../hooks/useAssetInventoryData';
import { assetInventoryAPI } from '../services/api';
import { buildCreatePayload } from '../utils/assetFormPayload';
import toast from '../utils/toast';
import DatePickerField from './UI/DatePickerField';
import { parseLocalDate, toLocalISODate } from '../utils/dateUtils';
import {
  CONDITION_OPTIONS,
  OWNERSHIP_OPTIONS,
  BRAND_SUGGESTIONS,
  WARRANTY_PROVIDER_SUGGESTIONS,
  SPEC_FIELD_CONFIG,
} from '../constants/assetOptions';

const ASSET_TAG_MAX_LENGTH = 50;
const CATEGORY_SPECIFIC_CODES = ['FLT', 'LIC', 'FUEL', 'DSL', 'ACC'];

const Section = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-gray-50 rounded-xl p-4 ${className}`}>
    <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
      {Icon && <Icon className="w-4 h-4" />}
      {title}
    </h4>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
      {children}
    </div>
  </div>
);

const EditField = ({ label, required, children, className = '' }) => (
  <div className={className}>
    <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </div>
    <div className="mt-1">{children}</div>
  </div>
);

const CUSTOM_FIELD_LABELS = {
  ram_gb: 'RAM (GB)',
  os_version: 'OS Version',
  storage_gb: 'Storage (GB)',
  cpu: 'CPU',
  size_inches: 'Size (inches)',
  resolution: 'Resolution',
};

const formatCustomFieldLabel = (key, schema) => {
  if (schema?.title) return schema.title;
  if (CUSTOM_FIELD_LABELS[key]) return CUSTOM_FIELD_LABELS[key];
  return String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const initialFormState = () => ({
  asset_type: '',
  asset_tag: '',
  serial_number: '',
  brand: '',
  model: '',
  description: '',
  condition: 'good',
  location: '',
  remarks: '',
  purchase_date: '',
  purchase_price: '',
  vendor: '',
  purchase_vendor: '',
  purchase_order_number: '',
  invoice_number: '',
  ownership_type: 'owned',
  warranty_start_date: '',
  warranty_end_date: '',
  warranty_provider: '',
  warranty_terms: '',
  custom_fields: {},
});

const AddAssetModal = ({ isOpen, onClose, onSuccess }) => {
  const [formState, setFormState] = useState(initialFormState);
  const { data: typesRaw = [] } = useAssetTypes();
  const createAsset = useCreateAsset();
  const { data: vendors = [] } = useVendors();
  const createVendor = useCreateVendor();
  const [showNewVendor, setShowNewVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorUen, setNewVendorUen] = useState('');
  const [uenLookupResult, setUenLookupResult] = useState(null);
  const [uenLookupLoading, setUenLookupLoading] = useState(false);

  const assetTypes = useMemo(() => {
    const list = Array.isArray(typesRaw) ? typesRaw : [];
    return list.filter((t) => t.is_active !== false);
  }, [typesRaw]);

  const assetTypeOptions = useMemo(() => {
    const byCategory = {};
    assetTypes.forEach((t) => {
      const name = t.category_name || t.category_code || 'Other';
      (byCategory[name] ||= []).push(t);
    });
    const opts = [];
    Object.entries(byCategory).forEach(([cat, list]) => {
      opts.push({ isGroupHeader: true, label: cat });
      list.forEach((t) => opts.push({ value: String(t.id), label: t.name }));
    });
    return opts;
  }, [assetTypes]);

  const warrantyProviderSuggestions = useMemo(() => {
    const vendorNames = (Array.isArray(vendors) ? vendors : []).map((v) => v.name);
    return [...WARRANTY_PROVIDER_SUGGESTIONS, ...vendorNames];
  }, [vendors]);

  const vendorOptions = useMemo(
    () =>
      (Array.isArray(vendors) ? vendors : []).map((v) => ({
        value: String(v.id),
        label: `${v.name}${v.uen ? ` (${v.uen})` : ''}`,
      })),
    [vendors]
  );

  const setField = useCallback((name, value) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const selectedType = useMemo(
    () => assetTypes.find((t) => String(t.id) === String(formState.asset_type)),
    [assetTypes, formState.asset_type]
  );
  const isPhysicalHandover = selectedType?.requires_physical_handover !== false;
  const isCategorySpecific = selectedType?.category_code && CATEGORY_SPECIFIC_CODES.includes(selectedType.category_code);

  const schemaProperties = useMemo(() => {
    const props = selectedType?.custom_fields_schema?.properties;
    if (!props || typeof props !== 'object') return null;
    const entries = Object.entries(props);
    return entries.length > 0 ? entries : null;
  }, [selectedType]);

  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      custom_fields: {},
      condition: isPhysicalHandover
        ? (prev.condition === 'not_applicable' ? 'good' : prev.condition)
        : 'not_applicable',
      location: isPhysicalHandover ? prev.location : '',
    }));
  }, [formState.asset_type, isPhysicalHandover]);

  const setCustomField = useCallback((key, value) => {
    setFormState((prev) => ({
      ...prev,
      custom_fields: { ...prev.custom_fields, [key]: value },
    }));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const tag = (formState.asset_tag || '').trim();
    if (!tag) {
      toast.error('Asset tag is required');
      return;
    }
    if (tag.length > ASSET_TAG_MAX_LENGTH) {
      toast.error(`Asset tag must be ${ASSET_TAG_MAX_LENGTH} characters or less`);
      return;
    }
    if (!formState.asset_type) {
      toast.error('Asset type is required');
      return;
    }
    const id = parseInt(formState.asset_type, 10);
    if (isNaN(id)) {
      toast.error('Please select a valid asset type');
      return;
    }

    const payload = buildCreatePayload(formState);
    createAsset.mutate(payload, {
      onSuccess: (created) => {
        onClose();
        if (typeof onSuccess === 'function') onSuccess(created);
      },
    });
  };

  const handleClose = () => {
    setFormState(initialFormState());
    setShowNewVendor(false);
    setNewVendorName('');
    setNewVendorUen('');
    setUenLookupResult(null);
    onClose();
  };

  const handleUenLookup = async () => {
    const uen = newVendorUen.trim().toUpperCase();
    if (!uen || uen.length < 8) {
      toast.error('Enter a valid UEN (8+ characters)');
      return;
    }
    setUenLookupLoading(true);
    try {
      const { data } = await assetInventoryAPI.verifyVendorUEN(uen);
      setUenLookupResult(data);
      if (data.already_exists) {
        setField('vendor', String(data.vendor.id));
        setShowNewVendor(false);
        toast.success(`Vendor already exists: ${data.vendor.name}`);
      } else if (data.verified) {
        setNewVendorName(data.entity_name);
        toast.success(`ACRA: ${data.entity_name}`);
      } else {
        toast.warning('UEN not found in ACRA – you can still add manually', { icon: '⚠️' });
      }
    } catch {
      toast.error('UEN lookup failed');
    } finally {
      setUenLookupLoading(false);
    }
  };

  const handleCreateVendor = () => {
    const name = newVendorName.trim();
    if (!name) { toast.error('Vendor name is required'); return; }
    createVendor.mutate(
      { name, uen: newVendorUen.trim().toUpperCase() || null },
      {
        onSuccess: (created) => {
          setField('vendor', String(created.id));
          setShowNewVendor(false);
          setNewVendorName('');
          setNewVendorUen('');
          setUenLookupResult(null);
          toast.success(`Vendor created: ${created.name}`);
        },
      }
    );
  };

  const noTypes = assetTypes.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Asset"
      size="lg"
      theme="admin"
      footer={
        !noTypes ? (
          <>
            <button type="button" onClick={handleClose} className="btn btn-admin-outline">
              Cancel
            </button>
            <button
              type="submit"
              form="add-asset-form"
              disabled={createAsset.isPending}
              className="btn btn-admin-primary"
            >
              {createAsset.isPending ? 'Creating…' : 'Create Asset'}
            </button>
          </>
        ) : undefined
      }
    >
      <form id="add-asset-form" onSubmit={handleSubmit} className="space-y-4">
              {noTypes ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-600 mb-2">No asset types defined.</p>
                  <p className="text-sm text-gray-600 mb-4">
                    Create an asset type in <strong>Categories</strong> first.
                  </p>
                  <Link
                    to="/asset-admin/categories"
                    className="btn btn-admin-primary"
                    onClick={handleClose}
                  >
                    Go to Categories
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  <Section title="Identification" icon={Package}>
                    <EditField label="Asset type" required>
                      <FormSelect
                        theme="admin"
                        options={assetTypeOptions}
                        value={formState.asset_type}
                        onChange={(val) => setField('asset_type', val)}
                        placeholder="Select type…"
                        required
                      />
                    </EditField>
                    <EditField label="Asset tag" required>
                      <input
                        type="text"
                        value={formState.asset_tag}
                        onChange={(e) => setField('asset_tag', e.target.value)}
                        className="input-admin w-full"
                        placeholder="e.g. WL-20230703"
                        maxLength={ASSET_TAG_MAX_LENGTH}
                      />
                    </EditField>
                    <EditField label="Serial number">
                      <input
                        type="text"
                        value={formState.serial_number}
                        onChange={(e) => setField('serial_number', e.target.value)}
                        className="input-admin w-full"
                      />
                    </EditField>
                    <EditField label="Brand">
                      <DatalistCombobox
                        value={formState.brand}
                        onChange={(val) => setField('brand', val)}
                        suggestions={BRAND_SUGGESTIONS}
                        placeholder="Select or type a brand"
                      />
                    </EditField>
                    <EditField label="Model">
                      <input
                        type="text"
                        value={formState.model}
                        onChange={(e) => setField('model', e.target.value)}
                        className="input-admin w-full"
                      />
                    </EditField>
                    {isPhysicalHandover && (
                      <>
                        <EditField label="Condition">
                          <FormSelect
                            theme="admin"
                            options={CONDITION_OPTIONS}
                            value={formState.condition}
                            onChange={(val) => setField('condition', val)}
                            placeholder="Select condition"
                          />
                        </EditField>
                        <EditField label="Location">
                          <input
                            type="text"
                            value={formState.location}
                            onChange={(e) => setField('location', e.target.value)}
                            className="input-admin w-full"
                            placeholder="Physical location if not assigned"
                          />
                        </EditField>
                      </>
                    )}
                    <EditField label="Description" className="sm:col-span-2">
                      <input
                        type="text"
                        value={formState.description}
                        onChange={(e) => setField('description', e.target.value)}
                        className="input-admin w-full"
                      />
                    </EditField>
                    <EditField label="Remarks" className="sm:col-span-2">
                      <input
                        type="text"
                        value={formState.remarks}
                        onChange={(e) => setField('remarks', e.target.value)}
                        className="input-admin w-full"
                      />
                    </EditField>
                  </Section>

                  {isCategorySpecific && (
                    <p className="text-xs text-amber-800 bg-amber-50 rounded-lg p-2">
                      Fleet, Licenses, Fuel Cards and Diesel Tokens often need extra data (e.g. vehicle number, license
                      seats). You can add that after creation or use the import tools.
                    </p>
                  )}

                  {schemaProperties && (
                    <Section title="Specifications" icon={Settings}>
                      {schemaProperties.map(([key, schemaDef]) => {
                        const hasEnum = Array.isArray(schemaDef.enum) && schemaDef.enum.length > 0;
                        const isNumeric = schemaDef.type === 'integer' || schemaDef.type === 'number';
                        const fieldConfig = SPEC_FIELD_CONFIG[key];
                        const currentValue = formState.custom_fields[key] ?? '';

                        // 1) Schema-defined enum always wins → closed dropdown.
                        const selectOptions = hasEnum
                          ? schemaDef.enum.map((opt) => ({
                              value: String(opt),
                              label: isNumeric && opt >= 1024 ? `${opt / 1024} TB` : String(opt),
                            }))
                          : fieldConfig?.kind === 'select'
                            ? fieldConfig.options
                            : null;

                        const isCombobox = !hasEnum && fieldConfig?.kind === 'combobox';

                        return (
                          <EditField key={key} label={formatCustomFieldLabel(key, schemaDef)}>
                            {selectOptions ? (
                              <FormSelect
                                theme="admin"
                                options={selectOptions}
                                value={currentValue === '' ? '' : String(currentValue)}
                                onChange={(val) => setCustomField(key, val)}
                                placeholder="— Select —"
                              />
                            ) : isCombobox ? (
                              <DatalistCombobox
                                value={currentValue}
                                onChange={(val) => setCustomField(key, val)}
                                suggestions={fieldConfig.suggestions}
                                placeholder={`Select or type ${formatCustomFieldLabel(key, schemaDef).toLowerCase()}`}
                              />
                            ) : (
                              <input
                                type={isNumeric ? 'number' : 'text'}
                                step={schemaDef.type === 'number' ? 'any' : schemaDef.type === 'integer' ? '1' : undefined}
                                min={isNumeric ? '0' : undefined}
                                value={currentValue}
                                onChange={(e) => setCustomField(key, e.target.value)}
                                className="input-admin w-full"
                              />
                            )}
                          </EditField>
                        );
                      })}
                    </Section>
                  )}

                  <Section title="Warranty" icon={Shield}>
                    <EditField label="Start date">
                      <DatePickerField
                        theme="admin"
                        selected={parseLocalDate(formState.warranty_start_date)}
                        onChange={(date) => setField('warranty_start_date', date ? toLocalISODate(date) : '')}
                        placeholderText="Select date"
                        isClearable
                      />
                    </EditField>
                    <EditField label="End date">
                      <DatePickerField
                        theme="admin"
                        selected={parseLocalDate(formState.warranty_end_date)}
                        onChange={(date) => setField('warranty_end_date', date ? toLocalISODate(date) : '')}
                        placeholderText="Select date"
                        isClearable
                      />
                    </EditField>
                    <EditField label="Provider">
                      <DatalistCombobox
                        value={formState.warranty_provider}
                        onChange={(val) => setField('warranty_provider', val)}
                        suggestions={warrantyProviderSuggestions}
                        placeholder="Select or type a provider"
                      />
                    </EditField>
                    <EditField label="Terms" className="sm:col-span-2">
                      <textarea
                        value={formState.warranty_terms}
                        onChange={(e) => setField('warranty_terms', e.target.value)}
                        className="input-admin w-full min-h-[60px]"
                        rows={2}
                      />
                    </EditField>
                  </Section>

                  <Section title="Purchase Information" icon={CreditCard}>
                    <EditField label="Date">
                      <DatePickerField
                        theme="admin"
                        selected={parseLocalDate(formState.purchase_date)}
                        onChange={(date) => setField('purchase_date', date ? toLocalISODate(date) : '')}
                        placeholderText="Select date"
                        isClearable
                      />
                    </EditField>
                    <EditField label="Price">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formState.purchase_price}
                        onChange={(e) => setField('purchase_price', e.target.value)}
                        className="input-admin w-full"
                        placeholder="0.00"
                      />
                    </EditField>
                    <EditField label="Vendor" className="sm:col-span-2">
                      {!showNewVendor ? (
                        <div className="flex gap-2 items-center">
                          <div className="flex-1">
                            <FormSelect
                              theme="admin"
                              options={vendorOptions}
                              value={formState.vendor}
                              onChange={(val) => setField('vendor', val)}
                              placeholder="— Select vendor —"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowNewVendor(true)}
                            className="btn btn-admin-outline text-xs flex gap-1 whitespace-nowrap"
                          >
                            <Plus className="w-3.5 h-3.5" /> New
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2 p-3 bg-white border border-gray-200 rounded-lg">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="UEN (optional)"
                              value={newVendorUen}
                              onChange={(e) => setNewVendorUen(e.target.value)}
                              className="input-admin flex-1"
                            />
                            <button
                              type="button"
                              onClick={handleUenLookup}
                              disabled={uenLookupLoading || !newVendorUen.trim()}
                              className="btn btn-admin-outline text-xs flex gap-1"
                            >
                              <Search className="w-3.5 h-3.5" />
                              {uenLookupLoading ? 'Looking up…' : 'Verify'}
                            </button>
                          </div>
                          {uenLookupResult?.verified && !uenLookupResult?.already_exists && (
                            <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                              <CheckCircle className="w-3.5 h-3.5" />
                              ACRA: {uenLookupResult.entity_name}
                            </div>
                          )}
                          <input
                            type="text"
                            placeholder="Vendor name *"
                            value={newVendorName}
                            onChange={(e) => setNewVendorName(e.target.value)}
                            className="input-admin w-full"
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              type="button"
                              onClick={() => { setShowNewVendor(false); setNewVendorName(''); setNewVendorUen(''); setUenLookupResult(null); }}
                              className="btn btn-admin-outline text-xs"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleCreateVendor}
                              disabled={createVendor.isPending || !newVendorName.trim()}
                              className="btn btn-admin-primary text-xs"
                            >
                              {createVendor.isPending ? 'Creating…' : 'Add Vendor'}
                            </button>
                          </div>
                        </div>
                      )}
                    </EditField>
                    <EditField label="PO number">
                      <input
                        type="text"
                        value={formState.purchase_order_number}
                        onChange={(e) => setField('purchase_order_number', e.target.value)}
                        className="input-admin w-full"
                      />
                    </EditField>
                    <EditField label="Invoice">
                      <input
                        type="text"
                        value={formState.invoice_number}
                        onChange={(e) => setField('invoice_number', e.target.value)}
                        className="input-admin w-full"
                      />
                    </EditField>
                    <EditField label="Ownership">
                      <FormSelect
                        theme="admin"
                        options={OWNERSHIP_OPTIONS}
                        value={formState.ownership_type}
                        onChange={(val) => setField('ownership_type', val)}
                        placeholder="Select ownership"
                      />
                    </EditField>
                  </Section>
                </div>
              )}
      </form>
    </Modal>
  );
};

export default AddAssetModal;
